import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Send } from "lucide-react";
import { useChatContext } from "@/contexts/ChatContext";
import { cn } from "@/lib/utils";
import atlasLogo from "@/assets/atlas-assistant-logo.png";

const FormatMessage = ({ content }: { content: string }) => {
  const lines = content.split("\n");
  return (
    <>
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (trimmed.startsWith("### "))
          return <h4 key={i} className="text-xs font-bold mt-2 mb-0.5">{trimmed.slice(4)}</h4>;
        if (trimmed.startsWith("## "))
          return <h3 key={i} className="text-sm font-bold mt-2 mb-0.5">{trimmed.slice(3)}</h3>;
        if (trimmed.startsWith("# "))
          return <h2 key={i} className="text-sm font-bold mt-2 mb-0.5">{trimmed.slice(2)}</h2>;
        if (trimmed === "---" || trimmed === "***")
          return <hr key={i} className="my-1.5 border-t border-border/40" />;
        if (trimmed.startsWith("- ") || trimmed.startsWith("• ")) {
          const text = trimmed.slice(2);
          return (
            <div key={i} className="flex items-start gap-1.5 ml-1 my-0.5">
              <span className="text-muted-foreground mt-px text-[10px]">•</span>
              <span className="flex-1"><BoldText text={text} /></span>
            </div>
          );
        }
        if (trimmed === "") return <div key={i} className="h-1" />;
        return <div key={i} className={i > 0 ? "mt-0.5" : ""}><BoldText text={line} /></div>;
      })}
    </>
  );
};

const BoldText = ({ text }: { text: string }) => {
  const parts = text.split(/(\*\*.+?\*\*)/g);
  return (
    <>
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**") && p.length > 4
          ? <strong key={i} className="font-semibold">{p.slice(2, -2)}</strong>
          : p ? <span key={i}>{p}</span> : null
      )}
    </>
  );
};

export function AtlasFloatingDock() {
  const {
    atlasDockOpen,
    closeAtlasDock,
    atlasConversation,
    sendAtlasMessage,
    atlasCreditsExhausted,
    openChats,
  } = useChatContext();
  const navigate = useNavigate();
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const messages = atlasConversation?.messages ?? [];

  // Autofocus on open
  useEffect(() => {
    if (atlasDockOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [atlasDockOpen]);

  // ESC to close
  useEffect(() => {
    if (!atlasDockOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") closeAtlasDock(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [atlasDockOpen, closeAtlasDock]);

  // Auto-scroll to newest
  useEffect(() => {
    if (!atlasDockOpen) return;
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length, atlasDockOpen]);

  if (!atlasDockOpen) return null;

  const handleSend = (e?: React.FormEvent) => {
    e?.preventDefault();
    const q = input.trim();
    if (!q || atlasCreditsExhausted) return;
    sendAtlasMessage(q);
    setInput("");
  };

  // Desktop offset: shift left of DM popouts (each w-80 = 320px + 8px gap, dock right-4 = 16px)
  const dmCount = openChats?.length ?? 0;
  const desktopRightPx = 16 + dmCount * (320 + 8);

  return (
    <div
      role="dialog"
      aria-label="Atlas Assistant"
      className={cn(
        "fixed z-50 flex flex-col overflow-hidden bg-background border border-border shadow-2xl",
        // Mobile: full-width bottom sheet
        "left-2 right-2 bottom-2 h-[75dvh] rounded-2xl",
        // Desktop: floating dock, dynamic right offset applied inline
        "md:left-auto md:h-[520px] md:w-[380px] md:rounded-xl md:bottom-4"
      )}
      style={{
        // Only applies on md+ (left-auto lets right take over)
        // Use CSS var fallback via inline style; browsers ignore extra on mobile
        // because `right` is overridden by left/right-2 utilities up to md.
        right: `${desktopRightPx}px`,
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-2 border-b flex-shrink-0"
        style={{ backgroundColor: "#02ffd3", borderColor: "hsl(var(--brand-teal-deep) / 0.25)" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <img src={atlasLogo} alt="Atlas" className="h-7 w-7 rounded-full object-contain flex-shrink-0" />
          <div className="min-w-0">
            <h4 className="text-[13px] font-bold text-foreground leading-tight">Atlas</h4>
            <p className="text-[10px] text-foreground/70 leading-tight truncate">AI freight assistant</p>
          </div>
        </div>
        <button
          type="button"
          onClick={closeAtlasDock}
          className="p-1 rounded hover:bg-black/10 transition-colors"
          aria-label="Close Atlas"
        >
          <X className="h-4 w-4 text-foreground/70" />
        </button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-2 min-h-0">
        {atlasCreditsExhausted && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-center">
            <p className="text-[11px] font-semibold text-destructive mb-1.5">You're out of Atlas credits</p>
            <p className="text-[11px] text-muted-foreground mb-2">Top up in Company Settings → Billing to keep asking.</p>
            <button
              type="button"
              onClick={() => { closeAtlasDock(); navigate("/company-settings"); }}
              className="text-[11px] font-medium underline text-primary hover:text-primary/80"
            >
              Go to Billing →
            </button>
          </div>
        )}

        {messages.length === 0 && !atlasCreditsExhausted && (
          <div className="text-[11px] text-muted-foreground text-center py-6">
            Ask Atlas anything about your loads, lanes, or market.
          </div>
        )}

        {messages.map((message) => {
          const isLoading = /^[🤔📊🔍💡]/.test(message.content);
          if (isLoading) {
            return (
              <div key={message.id} className="flex justify-start">
                <div className="max-w-[85%] px-3 py-2 rounded-lg bg-muted text-[11px]">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-0.5">
                      <div className="w-1.5 h-1.5 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "0ms", animationDuration: "1s" }} />
                      <div className="w-1.5 h-1.5 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "200ms", animationDuration: "1s" }} />
                      <div className="w-1.5 h-1.5 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "400ms", animationDuration: "1s" }} />
                    </div>
                    <span className="text-muted-foreground">{message.content}</span>
                  </div>
                </div>
              </div>
            );
          }
          return (
            <div key={message.id} className={cn("flex", message.isMe ? "justify-end" : "justify-start")}>
              <div className={cn(
                "max-w-[85%] px-3 py-2 rounded-lg text-[12px] leading-relaxed",
                message.isMe ? "bg-primary text-primary-foreground" : "bg-muted"
              )}>
                {message.isMe ? (
                  <span className="break-words">{message.content}</span>
                ) : (
                  <div className="break-words"><FormatMessage content={message.content} /></div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      <form onSubmit={handleSend} className="border-t p-2 flex items-center gap-2 flex-shrink-0 bg-background">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={atlasCreditsExhausted ? "Out of credits" : "Ask a follow-up…"}
          disabled={atlasCreditsExhausted}
          maxLength={500}
          className="flex-1 min-w-0 h-9 px-3 rounded-md border border-border bg-background text-[12px] focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={!input.trim() || atlasCreditsExhausted}
          className="h-9 w-9 flex items-center justify-center rounded-md bg-primary text-primary-foreground disabled:opacity-40 hover:bg-primary/90 transition-colors flex-shrink-0"
          aria-label="Send"
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
